import React, { useEffect, useState } from "react";
import useAxios from "../../../hooks/useAxios";
import "./StudentClassPage.css";
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Button,
  useMediaQuery,
} from "@mui/material";
import Header from "../../../components/Header/Header";
import QuizCard from "../../../components/QuizComponents/Cards/QuizCard/QuizCard";
import ActivateAccountAlert from "../../../components/Auth/ActivateAccountAlert/ActivateAccountAlert";
import ContentHeader from "../../../components/common/ContentHeader/ContentHeader";
import StudentAddQuizModal from "../../../components/DashboardComponents/Modals/StudentAddQuizModal/StudentAddQuizModal";
import QuizDueDateModal from "../../../components/DashboardComponents/Modals/QuizDueDateModal/QuizDueDateModal";
import { useTheme } from "@mui/material/styles";
import StudentReportTable from "../../../components/DashboardComponents/StudentDashboard/StudentReportTable/StudentReportTable";
import { useParams } from "react-router-dom";

const navLinks = [];

function StudentClassPage() {
  const { classId } = useParams();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const axiosInstance = useAxios();
  const [isActivateAlertOpen, setIsActivateAlertOpen] = useState(false);
  const [isAddQuizModalOpen, setIsAddQuizModalOpen] = useState(false);
  const [isDueDateModalOpen, setIsDueDateModalOpen] = useState(false);
  const [dueDateModalData, setDueDateModalData] = useState({
    quizTitle: "",
    dueDate: null,
  });
  const [reportData, setReportData] = useState([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Determine initial display count based on screen size
  let initialDisplayCount = 3;
  if (isSmallScreen) {
    initialDisplayCount = 1;
  } else if (isMediumScreen) {
    initialDisplayCount = 2;
  }

  const [showAllNew, setShowAllNew] = useState(false);

  const fetchQuizzes = async () => {
    try {
      const response = await axiosInstance.get(`/classes/student/${classId}`);
      if (response.data.success) {
        const { class: classData } = response.data; // Destructure the class object
        console.log("Class data:", classData); // Debug log
        console.log("Quizzes from class:", classData.quizzes); // Debug log
        console.log("Type of quizzes:", typeof classData.quizzes); // Debug log
        console.log("Is array?", Array.isArray(classData.quizzes)); // Debug log

        const quizzesArray = classData.quizzes || []; // Fallback to empty array if undefined
        console.log("Quizzes array after fallback:", quizzesArray); // Debug log

        const sortedQuizzes = quizzesArray
          .map((q) => ({ ...q, attempts_count: q.attempts_count ?? 0 })) // default to 0 if not present
          .sort((a, b) => a.attempts_count - b.attempts_count);

        setQuizzes(sortedQuizzes);
      } else {
        setError(response.data.message || "Failed to fetch Assessments.");
      }
    } catch (err) {
      console.error("Error fetching quizzes:", err);
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const response = await axiosInstance.get("/user/sessions");
      if (response.data.success) {
        setReportData(response.data.data);
      } else {
        setError(response.data.message || "Failed to fetch Assessments.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizzes();
    fetchReport();
  }, []);

  const handleEditQuiz = (quizId) => {
    setIsActivateAlertOpen(true);
  };

  const handleDeleteQuiz = (quizId) => {
    setIsActivateAlertOpen(true);
  };

  const handleAddQuiz = () => {
    setIsAddQuizModalOpen(true);
  };

  const handleModalClose = (refresh = false) => {
    setIsAddQuizModalOpen(false);
    if (refresh) {
      fetchQuizzes();
    }
  };

  const handleTakeQuiz = async (quiz) => {
    try {
      // Check quiz settings before allowing the student to take the quiz
      const response = await axiosInstance.get(
        `/quizzes/${quiz.id}/classes/${classId}/check-settings`
      );

      if (response.data.success && response.data.can_take_quiz) {
        // Quiz is available, proceed with creating the session
        try {
          const sessionResponse = await axiosInstance.post(`/sessions/create`, {
            quiz_id: quiz.id,
          });

          if (sessionResponse.data.success) {
            // Navigate to the session page with the session ID
            window.location.href = `/sessions/${sessionResponse.data.session.id}`;
          } else {
            setError(
              sessionResponse.data.message ||
                "Failed to start quiz. Please try again."
            );
          }
        } catch (sessionErr) {
          console.error("Error creating quiz session:", sessionErr);
          setError(
            sessionErr.response?.data?.message ||
              "Failed to start quiz. Please try again."
          );
        }
      } else {
        // Quiz is not available (due date passed or other restrictions)
        setDueDateModalData({
          quizTitle: quiz.title,
          dueDate: response.data.due_date,
        });
        setIsDueDateModalOpen(true);
      }
    } catch (err) {
      console.error("Error checking quiz settings:", err);
      if (
        err.response?.status === 403 &&
        err.response?.data?.message?.includes("due date")
      ) {
        // Due date has passed
        setDueDateModalData({
          quizTitle: quiz.title,
          dueDate: err.response.data.due_date,
        });
        setIsDueDateModalOpen(true);
      } else {
        setError(
          err.response?.data?.message ||
            "An unexpected error occurred while checking quiz availability."
        );
      }
    }
  };

  // Filter quizzes by attempts_count - only show quizzes that haven't been taken
  const newQuizzes = quizzes.filter((q) => q.attempts_count === 0);

  // Determine which quizzes to show
  const displayedQuizzes = showAllNew
    ? newQuizzes
    : newQuizzes.slice(0, initialDisplayCount);

  const totalQuizzes = newQuizzes.length;

  const isSubsetNew = !showAllNew && totalQuizzes > displayedQuizzes.length;

  let content;
  if (loading) {
    content = (
      <Box className="loading-container">
        <CircularProgress />
        <Typography variant="h6" className="loading-text">
          Fetching Assessments...
        </Typography>
      </Box>
    );
  } else if (error) {
    content = (
      <Box className="error-container">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  } else {
    content = (
      <>
        {/* New Quizzes Section */}
        <ContentHeader title="New Assessments" />
        <div className="quizzes-grid">
          {displayedQuizzes.length !== 0 ? (
            displayedQuizzes.map((quiz) => (
              <QuizCard
                key={quiz.id}
                quiz={quiz}
                isStudent={true}
                onEditClick={() => handleEditQuiz(quiz.id)}
                onDeleteClick={() => handleDeleteQuiz(quiz.id)}
                onTakeQuiz={() => handleTakeQuiz(quiz)}
              />
            ))
          ) : (
            <Typography>No new Assessments to show</Typography>
          )}
        </div>
        {(isSubsetNew ||
          (showAllNew && totalQuizzes > initialDisplayCount)) && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={2}
          >
            <Typography variant="body2" sx={{ mr: 2 }}>
              {showAllNew
                ? `Showing all ${totalQuizzes} Assessments`
                : `Showing ${displayedQuizzes.length} of ${totalQuizzes} Assessments`}
            </Typography>
            <Button
              className="text-button"
              onClick={() => setShowAllNew(!showAllNew)}
            >
              {showAllNew ? "View Less" : "View All"}
            </Button>
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <Container className="owner-quizzes-container page-container">
        {content}
        <ActivateAccountAlert
          open={isActivateAlertOpen}
          onClose={() => setIsActivateAlertOpen(false)}
        />
      </Container>
      <StudentAddQuizModal
        open={isAddQuizModalOpen}
        onClose={handleModalClose}
      />
      <QuizDueDateModal
        open={isDueDateModalOpen}
        onClose={() => setIsDueDateModalOpen(false)}
        quizTitle={dueDateModalData.quizTitle}
        dueDate={dueDateModalData.dueDate}
      />
      <StudentReportTable data={reportData} />
    </>
  );
}

export default StudentClassPage;
